import {
  GetDailyRevenueRequest,
  GetDailyRevenueResponse,
} from "@services/models/Analytics/GetDailyRevenue";
import {
  GetMerchantSuccessRatePayload,
  GetMerchantSuccessRateResponse,
} from "@services/models/Analytics/GetMerchantSuccessRate";
import { GetRevenueResponse } from "@services/models/Analytics/GetRevenueModel";
import {
  GetSystemSuccessRatePayload,
  GetSystemSuccessRateResponse,
} from "@services/models/Analytics/GetSystemSuccessRate";
import {
  GetUpiSuccessRatePayload,
  GetUpiSuccessRateResponse,
} from "@services/models/Analytics/GetUpiSuccessRate";
import {
  UpdatePaymentProofPrimeRequiredRequest,
  UpdatePaymentProofPrimeRequiredResponse,
} from "@services/models/Analytics/UpdatePaymentProofPrimeRequiredModel";
import {
  GetCashierTradeVolumeRequest,
  GetCashierTradeVolumeResponse,
} from "@services/models/Groups/GetCashierTradeVolumeModel";
import { GetBalanceSumResponse } from "@services/models/Prime/GetBalanceSumModel";
import { GetUnsweptBalanceModel } from "@services/models/Prime/GetUnsweptBalanceModel";
import { createApiClient } from "../api/createApiClient";
import { AxiosInstance } from "axios";

class AnalyticsApiService {
  private api: AxiosInstance;
  constructor() {
    this.api = createApiClient();
  }

  public async getSystemSuccessRate(
    payload: GetSystemSuccessRatePayload
  ): Promise<GetSystemSuccessRateResponse> {
    try {
      const response = await this.api.get(`/adminPanel/getSystemSuccessRate`, {
        params: {
          page: payload.page,
          rows: payload.rows,
          groupBy: payload.groupBy,
          startDate: payload.startDate,
          endDate: payload.endDate,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getMerchantSuccessRate(
    payload: GetMerchantSuccessRatePayload
  ): Promise<GetMerchantSuccessRateResponse> {
    try {
      const response = await this.api.get(
        `/adminPanel/getMerchantSuccessRate`,
        {
          params: {
            page: payload.page,
            rows: payload.rows,
            groupBy: payload.groupBy,
            startDate: payload.startDate,
            endDate: payload.endDate,
            merchantName: payload.merchantName,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getUpiSuccessRate(
    payload: GetUpiSuccessRatePayload
  ): Promise<GetUpiSuccessRateResponse> {
    try {
      const response = await this.api.get(`/adminPanel/getUPISuccessRate`, {
        params: {
          page: payload.page,
          rows: payload.rows,
          groupBy: payload.groupBy,
          startDate: payload.startDate,
          endDate: payload.endDate,
          search: payload.search,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getDailyRevenue(
    payload: GetDailyRevenueRequest
  ): Promise<GetDailyRevenueResponse> {
    try {
      const response = await this.api.get("/adminPanel/getDailyRevenue", {
        params: {
          page: payload.page,
          rows: payload.rows,
          merchantName: payload.merchantName,
          startDate: payload.startDate,
          endDate: payload.endDate,
          isMerchantWise: payload.isMerchantWise,
          rampType: payload.rampType,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getPersonalBalanceSum(): Promise<GetBalanceSumResponse> {
    try {
      const response = await this.api.get("/adminPanel/getPersonalBalanceSum");
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getMerchantBalanceSum(): Promise<GetBalanceSumResponse> {
    try {
      const response = await this.api.get("/adminPanel/getMerchantBalanceSum");
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getMerchantUnsweptBalance(): Promise<GetUnsweptBalanceModel> {
    try {
      const response = await this.api.get(
        "/adminPanel/getMerchantUnsweptBalance"
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getPersonalUnsweptBalance(): Promise<GetUnsweptBalanceModel> {
    try {
      const response = await this.api.get(
        "/adminPanel/getPersonalUnsweptBalance"
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getCashierTradeVolume(
    payload: GetCashierTradeVolumeRequest
  ): Promise<GetCashierTradeVolumeResponse> {
    try {
      const response = await this.api.get("/adminPanel/getCashierTradeVolume", {
        params: {
          page: 1,
          rows: 1000,
          startDate: payload.startDate,
          endDate: payload.endDate,
          groupId: payload.groupId,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getRevenue(): Promise<GetRevenueResponse> {
    try {
      const response = await this.api.get("/adminPanel/getRevenue");
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getRevenueDaily(): Promise<GetRevenueResponse> {
    try {
      const response = await this.api.get("/adminPanel/getRevenueDaily");
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async updatePaymentProofPrimeRequired(
    data: UpdatePaymentProofPrimeRequiredRequest
  ): Promise<UpdatePaymentProofPrimeRequiredResponse> {
    try {
      const response = await this.api.post(
        "/adminPanel/updatePaymentProofPrimeRequired",
        {
          merchantId: data.merchantId,
          paymentProofRequired: data.paymentProofRequired,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export const analyticsApis = new AnalyticsApiService();
